/**
 * gameTickerParts.jsx — presentational sub-components for GameTicker.
 * Extracted verbatim from components/GameTicker.jsx (god-file decomposition).
 * TickerChrome, SkeletonTickerItem, and TickerItem are exported for use by the
 * main GameTicker component; SkeletonShimmer, TeamLogo, and
 * LeagueSectionDivider are internal helpers used within this file.
 */

import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Platform, Animated, Image, TouchableOpacity, Easing } from 'react-native';
import { BlurView } from 'expo-blur';
import { Ionicons } from '@expo/vector-icons';
import { getTeamLogo } from '../data/logos';
import { styles } from './gameTickerStyles';
import { prefersReducedMotion } from '../utils/motion';
import { formatTickerName } from '../utils/tickerNames';

export const TickerChrome = ({ children }) =>
  Platform.OS === 'web' ? (
    <View style={[styles.blurContainer, styles.blurContainerWeb]}>{children}</View>
  ) : (
    <BlurView intensity={40} tint="dark" style={styles.blurContainer}>
      {children}
    </BlurView>
  );

// Skeleton shimmer animation component
const SkeletonShimmer = ({ width, height, borderRadius = 4, style }) => {
  const shimmerAnim = useRef(new Animated.Value(0)).current;
  
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const shimmer = Animated.loop(
      Animated.timing(shimmerAnim, {
        toValue: 1,
        duration: 1500,
        easing: Easing.linear,
        useNativeDriver: false,
      })
    );
    shimmer.start();
    return () => shimmer.stop();
  }, []);
  
  const translateX = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-width, width],
  });
  
  return (
    <View style={[{ 
      width, 
      height, 
      borderRadius,
      backgroundColor: 'rgba(255,255,255,0.08)',
      overflow: 'hidden',
    }, style]}>
      <Animated.View style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        transform: [{ translateX }],
      }}>
        <View style={{
          width: width * 0.5,
          height: '100%',
          backgroundColor: 'rgba(255,255,255,0.08)',
        }} />
      </Animated.View>
    </View>
  );
};

// Single skeleton ticker item
export const SkeletonTickerItem = ({ index }) => {
  return (
    <View style={styles.tickerItem}>
      {/* Status badge skeleton */}
      <SkeletonShimmer width={50} height={18} borderRadius={4} />
      
      {/* League skeleton */}
      <SkeletonShimmer width={30} height={12} borderRadius={3} />
      
      {/* Teams skeleton */}
      <View style={styles.teamsContainer}>
        <SkeletonShimmer width={18} height={18} borderRadius={9} />
        <SkeletonShimmer width={60} height={14} borderRadius={3} />
        <View style={{ width: 12 }} />
        <SkeletonShimmer width={60} height={14} borderRadius={3} />
        <SkeletonShimmer width={18} height={18} borderRadius={9} />
      </View>
      
      {/* Time skeleton */}
      <SkeletonShimmer width={70} height={12} borderRadius={3} />
      
      {/* Divider */}
      <View style={styles.divider} />
    </View>
  );
};

// Team logo placeholder - shows team initial
export const TeamLogo = ({ logoUrl, teamName, size = 20 }) => {
  const [hasError, setHasError] = useState(false);
  const initial = teamName ? teamName.charAt(0).toUpperCase() : '?';
  
  if (logoUrl && !hasError) {
    return (
      <Image 
        source={{ uri: logoUrl }} 
        style={{ width: size, height: size, borderRadius: size / 2 }}
        resizeMode="contain"
        onError={() => setHasError(true)}
      />
    );
  }
  
  // Fallback to initial
  return (
    <View style={{
      width: size,
      height: size,
      borderRadius: size / 2,
      backgroundColor: 'rgba(255,255,255,0.15)',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Text style={{
        fontSize: size * 0.5,
        fontWeight: '700',
        color: 'rgba(255,255,255,0.7)',
        fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
      }}>
        {initial}
      </Text>
    </View>
  );
};

// Helper to determine if name is likely a person (tennis, boxing, mma) vs team
const isPlayerSport = (league) => {
  const l = (league || '').toLowerCase();
  return l.includes('wta') || l.includes('atp') || l.includes('tennis') || 
         l.includes('ufc') || l.includes('mma') || l.includes('bellator') || l.includes('pfl') ||
         l.includes('boxing') || l.includes('golf') || l.includes('pga');
};

/** Short column label for the game card: "Celtics", "Red Sox", or a player's last name. */
export const shortTeamLabel = (fullName, league) => formatTickerName(fullName || 'TBD', 'short', !isPlayerSport(league), league);

// Helper to parse various date formats
export const parseDate = (startDate) => {
  if (!startDate) return null;
  
  try {
    // Handle Firestore timestamp format { _seconds, _nanoseconds }
    if (startDate._seconds) {
      return new Date(startDate._seconds * 1000);
    }
    // Handle Firestore timestamp format { seconds, nanoseconds }
    if (startDate.seconds) {
      return new Date(startDate.seconds * 1000);
    }
    // Handle ISO string or Date object
    return new Date(startDate);
  } catch (e) {
    console.warn('[GameTicker] Failed to parse date:', startDate);
    return null;
  }
};

// Helper function to format period/quarter/inning
export const formatPeriod = (period, league) => {
  const p = String(period).toLowerCase();
  const l = (league || '').toLowerCase();
  
  // Handle numeric periods
  const num = parseInt(p, 10);
  if (!isNaN(num)) {
    // Baseball uses "innings"
    if (l.includes('mlb') || l.includes('baseball')) {
      const suffix = num === 1 ? 'st' : num === 2 ? 'nd' : num === 3 ? 'rd' : 'th';
      return `${num}${suffix}`;
    }
    // Hockey uses "periods"
    if (l.includes('nhl') || l.includes('hockey')) {
      const suffix = num === 1 ? 'st' : num === 2 ? 'nd' : num === 3 ? 'rd' : 'th';
      return `${num}${suffix}`;
    }
    // Basketball/Football uses "quarters"
    const suffix = num === 1 ? 'st' : num === 2 ? 'nd' : num === 3 ? 'rd' : 'th';
    return `${num}${suffix}`;
  }
  
  // Handle text periods
  if (p.includes('half')) return p.includes('1') ? '1st Half' : '2nd Half';
  if (p.includes('ot') || p.includes('overtime')) return 'OT';
  if (p.includes('final')) return 'Final';
  
  return period;
};

// Helper function to determine game status.
// ⭐ In freeze/archive mode, pass referenceTime (ISO string or ms) so cached
// fixtures aren't marked as "ended" relative to the real wall clock.
export const getGameStatus = (startDate, referenceTime) => {
  const gameTime = parseDate(startDate);
  if (!gameTime) return { status: 'upcoming', label: 'UPCOMING', color: '#3B82F6', icon: 'time-outline' };
  
  try {
    const now = referenceTime ? new Date(referenceTime) : new Date();
    const diffMs = gameTime - now;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    // Game started more than 3 hours ago = ended
    if (diffHours < -3) {
      return { status: 'ended', label: 'FINAL', color: '#6B7280', icon: 'checkmark-circle-outline' };
    }
    // Game is live or recently started (within last 3 hours)
    else if (diffHours < 0) {
      return { status: 'live', label: 'LIVE', color: '#10B981', icon: 'radio-button-on', pulse: true };
    }
    // Game starts within next hour = starting soon
    else if (diffHours < 1) {
      return { status: 'soon', label: 'SOON', color: '#F59E0B', icon: 'alarm-outline' };
    }
    // Upcoming
    else {
      return { status: 'upcoming', label: 'UPCOMING', color: '#3B82F6', icon: 'time-outline' };
    }
  } catch (e) {
    return { status: 'upcoming', label: 'UPCOMING', color: '#3B82F6', icon: 'time-outline' };
  }
};

// Format game time
export const formatGameTime = (startDate) => {
  const gameTime = parseDate(startDate);
  if (!gameTime) return '';
  
  try {
    const now = new Date();
    const isToday = gameTime.toDateString() === now.toDateString();
    const isTomorrow = gameTime.toDateString() === new Date(now.getTime() + 86400000).toDateString();
    
    const timeStr = gameTime.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    if (isToday) return `Today ${timeStr}`;
    if (isTomorrow) return `Tomorrow ${timeStr}`;
    
    return gameTime.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (e) {
    return '';
  }
};

// ⭐ League Section Divider - Bold visual separator between leagues
const LeagueSectionDivider = ({ league, isStart = true }) => {
  return (
    <View style={styles.leagueDividerContainer}>
      {/* Gradient bar */}
      <View style={styles.leagueDividerBar} />
      {/* League badge */}
      <View style={styles.leagueDividerBadge}>
        <Text style={styles.leagueDividerText}>{league}</Text>
      </View>
      {/* Gradient bar */}
      <View style={styles.leagueDividerBar} />
    </View>
  );
};

const IS_WEB = Platform.OS === 'web';

/**
 * Single game item in the ticker. Memoised: GameTicker passes stable
 * callbacks, so opening the game card re-renders only the rows whose
 * `cardActive` changed, not the whole strip.
 *
 * Web only: hover / keyboard focus / tap report to GameTicker, which owns the
 * game card (GamePopover). `onGamePress(fixture, chipKey, el)` decides whether
 * a press searches (mouse, keyboard) or toggles the card (touch).
 */
export const TickerItem = React.memo(function TickerItem({
  fixture, onGamePress, onSGPPress, onLivePress, index, nameFormat, hasCachedSGPs = false, showSGPIndicator = false,
  liveScore = null, isFirstInLeague = false, isLastInLeague = false, onRowLayout, referenceTime = null, decorative = false,
  chipKey, cardId, cardActive = false, onCardHoverIn, onCardHoverOut, onCardFocus, onCardBlur, onCardKeyDown, onCardPointerDown,
  // eslint-disable-next-line no-unused-vars
  statusKey, // LIVE/SOON/UPCOMING: a change re-renders this memoised row so its badge stays current
}) {
  const status = getGameStatus(fixture.start_date, referenceTime);
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const chipRef = useRef(null);
  // Stays a plain focusable element (not role="button", which RNW renders as a
  // <button> and would flatten the LIVE / SGP controls inside it for screen readers).
  const cardHooks = IS_WEB && onCardHoverIn ? {
    'aria-haspopup': decorative ? undefined : 'dialog',
    'aria-expanded': decorative ? undefined : !!cardActive,
    'aria-describedby': cardActive && !decorative ? cardId : undefined,
    dataSet: { gameChip: decorative ? 'copy' : 'true' },
    onPointerDown: (e) => onCardPointerDown && onCardPointerDown(e?.nativeEvent?.pointerType || e?.pointerType),
    onMouseEnter: () => onCardHoverIn(chipKey, fixture, chipRef.current),
    onMouseLeave: onCardHoverOut,
    onFocus: (e) => onCardFocus && onCardFocus(chipKey, fixture, chipRef.current, e),
    onBlur: onCardBlur,
    onKeyDownCapture: (e) => onCardKeyDown && onCardKeyDown(e, chipKey),
  } : {};
  
  // Pulse animation for live games
  useEffect(() => {
    if (status.pulse && !prefersReducedMotion()) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 0.5,
            duration: 800,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 800,
            useNativeDriver: true,
          }),
        ])
      );
      pulse.start();
      return () => pulse.stop();
    }
  }, [status.pulse]);

  const homeTeamFull = fixture.home_team || 'TBD';
  const awayTeamFull = fixture.away_team || 'TBD';
  const league = (fixture.league || '').toLowerCase();
  
  // Better sport detection from league
  const detectSport = (leagueStr) => {
    const l = leagueStr.toLowerCase();
    if (l.includes('nba') || l.includes('ncaab') || l.includes('wnba')) return 'basketball';
    if (l.includes('nfl') || l.includes('ncaaf') || l.includes('cfl')) return 'football';
    if (l.includes('mlb')) return 'baseball';
    if (l.includes('nhl')) return 'hockey';
    if (l.includes('mls') || l.includes('epl') || l.includes('premier') || l.includes('la liga') || l.includes('bundesliga') || l.includes('serie a') || l.includes('champions') || l.includes('uefa')) return 'soccer';
    return fixture.sport || 'basketball';
  };
  
  const sport = detectSport(league);
  const isPlayer = isPlayerSport(league);
  
  // Format names based on user preference
  const homeTeam = formatTickerName(homeTeamFull, nameFormat, !isPlayer, league);
  const awayTeam = formatTickerName(awayTeamFull, nameFormat, !isPlayer, league);
  
  // Get logos - try fixture data first, then fall back to getTeamLogo lookup
  const homeLogo = fixture.home_logo || fixture.home_team_logo || fixture.home_image || getTeamLogo(homeTeamFull, sport);
  const awayLogo = fixture.away_logo || fixture.away_team_logo || fixture.away_image || getTeamLogo(awayTeamFull, sport);
  
  return (
    <View
      style={{ flexDirection: 'row', alignItems: 'center' }}
      onLayout={onRowLayout}
      collapsable={false}
      aria-hidden={decorative || undefined}
    >
      {/* ⭐ League Section Start Divider */}
      {isFirstInLeague && (
        <LeagueSectionDivider league={league.toUpperCase()} isStart={true} />
      )}
      
      <TouchableOpacity
        ref={chipRef}
        onPress={() => { if (onGamePress) onGamePress(fixture, chipKey, chipRef.current); }}
        activeOpacity={0.7}
        style={[styles.tickerItem, cardActive && styles.tickerItemActive]}
        focusable={!decorative}
        {...cardHooks}
      >
        {/* Status Badge — LIVE is a clickable button, others are static */}
        {status.status === 'live' ? (
          <TouchableOpacity
            style={[styles.statusBadge, { backgroundColor: `${status.color}20`, borderWidth: 1, borderColor: `${status.color}40` }, Platform.OS === 'web' && { cursor: 'pointer' }]}
            onPress={(e) => { e.stopPropagation(); if (onLivePress) onLivePress(fixture); }}
            activeOpacity={0.7}
            focusable={!decorative}
          >
            <Animated.View style={[styles.liveBadgeDot, { opacity: pulseAnim }]} />
            <Text style={[styles.statusText, { color: status.color }]}>{status.label}</Text>
          </TouchableOpacity>
        ) : (
          <View style={[styles.statusBadge, { backgroundColor: `${status.color}20` }]}>
            <Animated.View style={status.pulse ? { opacity: pulseAnim } : null}>
              <Ionicons name={status.icon} size={10} color={status.color} />
            </Animated.View>
            <Text style={[styles.statusText, { color: status.color }]}>{status.label}</Text>
          </View>
        )}
      
      {/* League Tag */}
      {league && (
        <Text style={styles.leagueTag}>{league.toUpperCase()}</Text>
      )}
      
      {/* Teams with Logos and Live Score */}
      <View style={styles.teamsContainer}>
        <TeamLogo logoUrl={awayLogo} teamName={awayTeamFull} size={18} />
        <Text style={styles.teamText} numberOfLines={1}>{awayTeam}</Text>
        
        {/* Live Score Display - Glassmorphic */}
        {liveScore && liveScore.is_live && liveScore.away_score !== null ? (
          <View style={styles.liveScoreContainer}>
            <View style={styles.liveScoreInner}>
              <Text style={styles.liveScoreText}>{liveScore.away_score}</Text>
              <Text style={styles.liveScoreDash}>-</Text>
              <Text style={styles.liveScoreText}>{liveScore.home_score}</Text>
            </View>
            {liveScore.period && (
              <Text style={styles.livePeriodText}>
                {formatPeriod(liveScore.period, league)}
              </Text>
            )}
          </View>
        ) : (
          <Text style={styles.vsText}>@</Text>
        )}
        
        <Text style={styles.teamText} numberOfLines={1}>{homeTeam}</Text>
        <TeamLogo logoUrl={homeLogo} teamName={homeTeam} size={18} />
      </View>
      
      {/* Time (hide if showing live score with period) */}
      {(!liveScore || !liveScore.is_live) && (
        <Text style={styles.timeText}>{formatGameTime(fixture.start_date)}</Text>
      )}
      
      {/* ⭐ SGP Cache Indicator - clickable to view SGPs */}
      {showSGPIndicator && hasCachedSGPs && (
        <TouchableOpacity 
          style={styles.sgpIndicator}
          onPress={(e) => {
            e.stopPropagation();
            if (onSGPPress) onSGPPress(fixture);
          }}
          activeOpacity={0.7}
          focusable={!decorative}
        >
          <View style={styles.sgpDot} />
          <Text style={styles.sgpIndicatorText}>SGP</Text>
        </TouchableOpacity>
      )}

      {/* LIVE badge moved inline to status badge slot above */}
      
      {/* Divider - bolder if last in league */}
      <View style={[styles.divider, isLastInLeague && styles.leagueEndDivider]} />
    </TouchableOpacity>
    </View>
  );
});
